import { Box } from '@mui/material'
import React from 'react'
import ContentWrapper from '../components/ContentWrapper'
import SimpleTable from '../components/SimpleTable'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { DeliverySlot } from '../utils/deliverySlot'
import { Community } from '../utils/community'
import Txt from './Txt'
import Card from './Card'

const DELIVERY_SLOT_DICT = {
	dayDate: 'Tag / Datum',
	time: 'Uhrzeit',
	community: 'Gemeinde',
	occupiedSeats: 'Belegte Sitze',
	totalSeats: 'Anzahl Sitze',
	closesAt: 'Buchbar bis',
}

const DATE_LOCALE = { locale: de }

const DeliverySlotsHeader = () => (
	<Box
		display="flex"
		flexDirection="row"
		justifyContent="space-between"
		alignItems="flex-start"
		gap="1rem"
	>
		<Txt variant="h4" fontWeight="bold" m="0" color="primary.main">
			Lieferzeitpunkte
		</Txt>
	</Box>
)

const EditDeliverySlots = ({
	deliverySlots,
	communities,
}: {
	deliverySlots?: DeliverySlot[]
	communities?: Community[]
}): JSX.Element => {
	const sortedDeliverySlots = React.useMemo(() => {
		if (!deliverySlots || deliverySlots.length === 0) return []
		return deliverySlots
			.slice()
			.sort((a, b) => new Date(a.deliveryTime).getTime() - new Date(b.deliveryTime).getTime())
	}, [deliverySlots])

	const communityNameById = React.useMemo(() => {
		const entries = (communities || []).map(
			(community) => [String(community.id), community.title] as const
		)
		return new Map(entries)
	}, [communities])

	const deliverySlotRows = React.useMemo(() => {
		if (!sortedDeliverySlots.length) return []
		return sortedDeliverySlots.map((slot) => {
			const totalSeats =
				typeof slot.bookingInfo?.nTotalSeats === 'number' ? slot.bookingInfo.nTotalSeats : null
			const occupiedSeats =
				typeof slot.bookingInfo?.nAuthorizedSeats === 'number'
					? slot.bookingInfo.nAuthorizedSeats
					: 0
			const occupiedLabel = totalSeats === null ? '-' : String(occupiedSeats)
			const totalLabel = totalSeats === null ? '-' : String(totalSeats)
			const deliveryDate = parseISO(slot.deliveryTime)
			const communityId = slot.bookingInfo?.communityId
			const communityLabel =
				communityId !== undefined && communityId !== null
					? communityNameById.get(String(communityId)) || String(communityId)
					: '-'
			return {
				id: slot.id,
				__type: 'deliverySlot',
				dayDate: `${format(deliveryDate, 'EEEE', DATE_LOCALE)} · ${format(
					deliveryDate,
					'dd.MM.yyyy'
				)}`,
				time: format(deliveryDate, 'HH:mm'),
				community: communityLabel,
				occupiedSeats: occupiedLabel,
				totalSeats: totalLabel,
				closesAt: slot.closesAt ? format(parseISO(slot.closesAt), 'HH:mm') : '–',
			}
		})
	}, [communityNameById, sortedDeliverySlots])

	const deliverySlotActiveKeys = React.useMemo(
		() => ['dayDate', 'time', 'community', 'occupiedSeats', 'totalSeats', 'closesAt'],
		[]
	)

	return (
		<>
			<Box maxWidth="100%">
				<Card mb="3rem">
					<DeliverySlotsHeader />
					<Txt variant="body2" color="text.secondary" mt="0.35rem">
						Anzeige der Lieferzeitpunkte aus der Slot-Tabelle inklusive Gemeinde.
					</Txt>
					{deliverySlotRows.length ? (
						<Box mt="1rem">
							<SimpleTable
								objects={deliverySlotRows}
								dict={DELIVERY_SLOT_DICT}
								activeKeys={deliverySlotActiveKeys}
								disableExpandIcons
								disablePagination
								emptyRowsFallBackContextType="deliverySlot"
							/>
						</Box>
					) : (
						<Box mt="1rem">
							<Txt variant="body2" color="text.secondary">
								Keine Lieferzeitpunkte vorhanden.
							</Txt>
						</Box>
					)}
				</Card>
			</Box>
		</>
	)
}

export default EditDeliverySlots
